import { lazy, Suspense } from 'react';
import { Outlet } from 'react-router';
import { ProjectNavModePreferenceProvider } from '@gonfalon/chrome/project-nav-mode-preference-provider';
import {
  enableOneTimeAnnouncementSystem,
  enableOpenSeatsSnackbarNotification,
  isSkipToContentEnabled,
} from '@gonfalon/dogfood-flags/dogfood-flags';
import { NoisyErrorBoundary } from '@gonfalon/error-boundaries/NoisyErrorBoundary';
import { OneTimeAnnouncement } from '@gonfalon/in-product-announcement/OneTimeAnnouncement';
import { SnackbarRegion, ToastRegion } from '@launchpad-ui/components';

import { Announcer } from './Announcer';
import { SkipToContent } from './SkipToContent/SkipToContent';

const OpenSeatNotification = lazy(async () =>
  import(/* webpackChunkName: "OpenSeatNotification" */ '#components/openSeatNotification/OpenSeatNotification').then(
    (module) => ({
      default: module.OpenSeatNotification,
    }),
  ),
);

const OneTimeNotificationModals = lazy(async () =>
  import(
    /* webpackChunkName: "OneTimeNotificationModals" */ '#components/oneTimeNotificationModals/OneTimeNotificationModals'
  ).then((module) => ({
    default: module.OneTimeNotificationModals,
  })),
);

const InviteMembersModalContainer = lazy(async () =>
  import(
    /* webpackChunkName: "InviteMembersModalContainer" */ '#components/inviteMembersLegacy/InviteMembersModalContainer'
  ).then((module) => ({
    default: module.InviteMembersModalContainer,
  })),
);

const SuggestInviteMembersModalContainer = lazy(async () =>
  import(
    /* webpackChunkName: "SuggestInviteMembersModalContainer" */ '#components/inviteMembersLegacy/SuggestInviteMembersModalContainer'
  ).then((module) => ({
    default: module.SuggestInviteMembersModalContainer,
  })),
);

const NotificationCenterContainer = lazy(() =>
  import(/* webpackChunkName: "NotificationCenterContainer" */ '#components/NotificationCenterContainer').then(
    (mod) => ({
      default: mod.NotificationCenterContainer,
    }),
  ),
);

export type AppShellProps = {};

/**
 * AppShell owns shared top-level components that are part of our app. No matter where
 * the user is, we should be able to render these above everything else.
 */
export function AppShell() {
  return (
    <div>
      {isSkipToContentEnabled() && <SkipToContent />}
      <Announcer />
      {enableOneTimeAnnouncementSystem() && <OneTimeAnnouncement placement="top start" />}

      <NoisyErrorBoundary severity="critical">
        <Suspense fallback={null}>
          <OneTimeNotificationModals />
        </Suspense>

        <Suspense fallback={null}>
          <InviteMembersModalContainer />
        </Suspense>
        <Suspense fallback={null}>
          <SuggestInviteMembersModalContainer />
        </Suspense>

        {enableOpenSeatsSnackbarNotification() && (
          <Suspense fallback={null}>
            <OpenSeatNotification />
          </Suspense>
        )}

        <Suspense fallback={null}>
          <NotificationCenterContainer />
        </Suspense>

        <SnackbarRegion />
        <ToastRegion />
      </NoisyErrorBoundary>

      {/* Above every route on purpose. See ProjectNavModePreferenceProvider. */}
      <ProjectNavModePreferenceProvider>
        <Suspense fallback={null}>
          <Outlet />
        </Suspense>
      </ProjectNavModePreferenceProvider>
    </div>
  );
}
